import OpenSidebarIcon from '@assets/images/common/icon/channel/open_sidebar.svg';
import { MobileSidebarContext } from '@src/context/MobileSidebarContext';
import SearchHistoryDropdown from '@src/features/shared/community/components/search/SearchHistoryDropdown';
import { useSearchHistory } from '@src/hooks/useSearchHistory';
import InputSearch from '@src/ui/elements/form/InputSearch';
import { useContext } from 'react';

interface CommunityChatSearchHeaderProps {
  searchValue: string;
  setSearchValue: (value: string) => void;
  keyword?: string;
  handleSearch: () => void;
  clearSearch: () => void;
  onHistoryItemSelect?: (selectedItem?: string) => void;
}

function CommunityChatSearchHeader({
  searchValue,
  setSearchValue,
  keyword,
  handleSearch,
  clearSearch,
  onHistoryItemSelect,
}: CommunityChatSearchHeaderProps) {
  const { toggle: toggleMobileSidebar } = useContext(MobileSidebarContext);
  const {
    showHistory,
    removeFromHistory,
    clearHistory,
    handleInputFocus,
    handleInputChange,
    handleInputBlur,
    hideHistoryImmediately,
    getFilteredHistory,
  } = useSearchHistory();

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      hideHistoryImmediately();
      handleSearch();
    } else if (e.key === 'Escape') {
      hideHistoryImmediately();
    }
  };

  const handleInputValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchValue(newValue);
    handleInputChange();
  };

  const handleInputFocusEvent = (e: React.FocusEvent<HTMLInputElement>) => {
    handleInputFocus(e.target.value);
  };

  const handleHistoryItemClick = (item: string) => {
    setSearchValue(item);
    hideHistoryImmediately();
    if (onHistoryItemSelect) {
      onHistoryItemSelect(item);
    }
  };

  const handleHistoryItemRemove = (item: string) => {
    removeFromHistory(item);
  };

  const handleClearHistory = () => {
    clearHistory();
  };

  const handleClearSearch = () => {
    clearSearch();
    setTimeout(() => {
      handleInputChange();
    }, 50);
  };

  return (
    <div className='border-b border-gray-200 px-6 py-[5px] lg:py-[9px] lg:pl-2 lg:pr-3'>
      <div className='flex w-full min-w-0 grow items-start rounded-[2px] lg:items-center [&>div]:w-full'>
        <button
          className='hidden size-[32px] flex-[0_0_auto] place-items-center lg:grid'
          onClick={toggleMobileSidebar}
          data-setting-menu=''
        >
          <img
            src={OpenSidebarIcon}
            alt=''
            width={16}
            height={16}
            className='size-[16px]'
          />
        </button>
        <div className='relative w-full'>
          <InputSearch
            value={searchValue}
            defaultValue={keyword || ''}
            onChange={handleInputValueChange}
            onSubmit={() => handleSearch()}
            onKeyDown={handleSearchKeyDown}
            onFocus={handleInputFocusEvent}
            onBlur={handleInputBlur}
            onClear={handleClearSearch}
          />
          {showHistory && getFilteredHistory(searchValue).length > 0 && (
            <SearchHistoryDropdown
              items={getFilteredHistory(searchValue)}
              searchValue={searchValue}
              onItemClick={handleHistoryItemClick}
              onItemRemove={handleHistoryItemRemove}
              onClearHistory={handleClearHistory}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default CommunityChatSearchHeader;
